import { useMemo } from "react";
import {
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Build as BuildIcon,
  AddCircleOutline as AddIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Speed as SpeedIcon,
} from "@mui/icons-material";
import type { MenuOptionType } from "@/controls/contextMenu/models/MenuOptionType";
import { PART_IDS, type RoofTreeRow } from "../models";

export type ContextMenuAction =
  | "install"
  | "uninstall"
  | "repair"
  | "newSparePart"
  | "editPart"
  | "setInitialMetric";

interface UseRoofContextMenuProps {
  onInstall: (row: RoofTreeRow) => void;
  onUninstall: (row: RoofTreeRow) => void;
  onRepair: (row: RoofTreeRow) => void;
  onNewSparePart: (row: RoofTreeRow) => void;
  onEditPart: (row: RoofTreeRow) => void;
  onSetInitialMetric: (row: RoofTreeRow) => void;
  onCorrectDates: (row: RoofTreeRow) => void;
}

// Top-level containers that should not show context menu
const EXCLUDED_CONTAINERS: number[] = [
  PART_IDS.ROOF_SPARES,
  PART_IDS.ROOF_PARTS_SPARES,
];

export const useRoofContextMenu = ({
  onInstall,
  onUninstall,
  onRepair,
  onNewSparePart,
  onEditPart,
  onSetInitialMetric,
  onCorrectDates,
}: UseRoofContextMenuProps) => {
  const getMenuOptions = useMemo(
    () => (data: RoofTreeRow) => {
      const options: MenuOptionType[] = [];

      if (!data) return options;

      // Context menu for spares containers (except top-level) and spare parts
      if (data.rowType === "sparesContainer" || data.rowType === "sparePart") {
        // Exclude top-level containers (Roof Spares, Roof Parts Spares)
        const containerId = data.ContainerId ?? data.EquipmentId;
        if (EXCLUDED_CONTAINERS.includes(containerId)) {
          return options;
        }

        options.push({
          name: "New Spare Part",
          key: "newSparePart",
          onClick: () => onNewSparePart(data),
          icon: <AddIcon fontSize="small" color="primary" />,
        });

        // Add "Edit Part", "Repair", "Set Initial Metric" and "Delete" options for spare parts
        if (data.rowType === "sparePart" && data.L2PartId) {
          options.push({
            name: "Edit Part",
            key: "editPart",
            onClick: () => onEditPart(data),
            icon: <EditIcon fontSize="small" color="primary" />,
          });
          options.push({
            name: "Repair",
            key: "repair",
            onClick: () => onRepair(data),
            icon: <BuildIcon fontSize="small" color="warning" />,
          });
          options.push({
            name: "Set Initial Metric",
            key: "setInitialMetric",
            onClick: () => onSetInitialMetric(data),
            icon: <SpeedIcon fontSize="small" color="info" />,
          });
          options.push({
            name: "Correct Dates",
            key: "correctDates",
            onClick: () => onCorrectDates(data),
            icon: <ScheduleIcon fontSize="small" color="primary" />,
          });
        }

        return options;
      }

      // Context menu for equipment rows (roof assemblies)
      if (data.rowType === "equipment") {
        options.push({
          name: "Set Initial Metric",
          key: "setInitialMetric",
          onClick: () => onSetInitialMetric(data),
          icon: <SpeedIcon fontSize="small" color="info" />,
        });
        options.push({
          name: "Correct Dates",
          key: "correctDates",
          onClick: () => onCorrectDates(data),
          icon: <ScheduleIcon fontSize="small" color="primary" />,
        });
        return options;
      }

      // Context menu for location rows (panel positions on roofs)
      if (data.rowType !== "location") {
        return options;
      }

      const hasPart = data.HasAssignedPart ?? false;

      if (!hasPart) {
        // Show "Install Part" option for empty positions
        options.push({
          name: "Install Part",
          key: "install",
          onClick: () => onInstall(data),
          icon: <LinkIcon fontSize="small" color="primary" />,
        });
      } else {
        // Show "Uninstall Part", "Repair", "Edit Part" and "Set Initial Metric" options for positions with parts
        options.push({
          name: "Uninstall Part",
          key: "uninstall",
          onClick: () => onUninstall(data),
          icon: <LinkOffIcon fontSize="small" color="error" />,
        });
        options.push({
          name: "Repair",
          key: "repair",
          onClick: () => onRepair(data),
          icon: <BuildIcon fontSize="small" color="warning" />,
        });
        options.push({
          name: "Edit Part",
          key: "editPart",
          onClick: () => onEditPart(data),
          icon: <EditIcon fontSize="small" color="primary" />,
        });
        options.push({
          name: "Set Initial Metric",
          key: "setInitialMetric",
          onClick: () => onSetInitialMetric(data),
          icon: <SpeedIcon fontSize="small" color="info" />,
        });
        options.push({
          name: "Correct Dates",
          key: "correctDates",
          onClick: () => onCorrectDates(data),
          icon: <ScheduleIcon fontSize="small" color="primary" />,
        });
      }

      return options;
    },
    [
      onInstall,
      onUninstall,
      onRepair,
      onNewSparePart,
      onEditPart,
      onSetInitialMetric,
      onCorrectDates,
    ],
  );

  return {
    getMenuOptions,
  };
};
